/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdPassphrase — passphrase creation form. Six-word UI with per-slot
 * random buttons + a "generate all" button. Wordlists are lazy-loaded
 * via the registry (`src/wordlists/index.js`); random word picks route
 * through the F3 RNG seam (via `pickRandomWord`).
 *
 * Dispatches two component-level events on save:
 *   - `passphrase-saved` (legacy name, scoped to UI tab switching)
 *   - `new-vault-passphrase` (consumed by main.js → vaultService.createVault)
 *
 * `new-vault-passphrase` is also registered on the typed
 * `EventDispatchMap` (#161) per the #172 AC — even though the dispatch
 * uses component-level `dispatchEvent`, centralising the type for the
 * event name keeps future consumers aligned if they migrate to the
 * EventDispatch singleton.
 *
 * Lit light-DOM component (#206). The template lives in `render()`; the
 * language `<wa-option>` list comes from `ALL_LANGUAGES` via `.map()`.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  ALL_LANGUAGES,
  loadWordlist,
  pickRandomWord,
  type LanguageCode,
} from '../wordlists/index.js';
import { t, LocaleController, setUiLocale } from '../services/i18n.js';

/**
 * Subset of TdSetup's state-machine names this component advances to.
 * Kept local to avoid a circular import on `TdSetup`'s exported type.
 * The ancestor's `state` setter validates at runtime regardless.
 */
type AdvancedSetupState = 'retrievingDisclosures';

/** Detail payload for both `passphrase-saved` and `new-vault-passphrase`. */
export interface NewVaultPassphraseDetail {
  passphrase: string;
  words: string[];
  language: LanguageCode;
}

// #161/#172: declare the typed event-name → payload mapping for the
// EventDispatch singleton. Today the component-local `dispatchEvent`
// path doesn't read this mapping, but registering it here makes the
// contract authoritative for future consumers + the typed
// `EventDispatch.subscribe` overloads.
declare module '../services/eventDispatch.js' {
  interface EventDispatchMap {
    'new-vault-passphrase': NewVaultPassphraseDetail;
  }
}

interface WaInputElement extends HTMLElement {
  value: string;
}

interface WaSelectElement extends HTMLElement {
  value: string;
}

const isSourcedLanguage = (code: string): boolean =>
  ALL_LANGUAGES.some(l => l.code === code && l.isSourced);

@customElement('td-passphrase')
export class TdPassphrase extends LitElement {
  @state() selectedLanguage: LanguageCode = 'en';

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  /** Wordlist for the active language; lazily loaded. */
  activeWordlist: { code: LanguageCode; words: readonly string[] } | null =
    null;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Internal tab-switch wiring: the same-host `passphrase-saved`
    // dispatch on this element won't reach an `@event` binding on a
    // child node (events bubble upwards from their target only).
    this.addEventListener('passphrase-saved', this._handlePassphraseSaved);
  }

  /**
   * Lazy-load the active language's wordlist. Cached on `this.activeWordlist`
   * after the first successful load. No-op if already loaded for the same
   * language.
   */
  async ensureWordlistLoaded(): Promise<readonly string[]> {
    if (
      this.activeWordlist &&
      this.activeWordlist.code === this.selectedLanguage
    ) {
      return this.activeWordlist.words;
    }
    const words = await loadWordlist(this.selectedLanguage);
    this.activeWordlist = { code: this.selectedLanguage, words };
    return words;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    console.log('TdPassphrase disconnected');
  }

  nextState(element: HTMLElement, stateName: AdvancedSetupState): void {
    let ancestor: HTMLElement | null = element.parentElement;
    while (ancestor && ancestor.tagName !== 'TD-SETUP') {
      ancestor = ancestor.parentElement;
    }

    if (ancestor && ancestor.tagName === 'TD-SETUP') {
      (ancestor as HTMLElement & { state: AdvancedSetupState }).state =
        stateName;
    }

    if (element.parentNode) {
      element.parentNode.removeChild(element);
    }
  }

  private _handleLanguageChange = async (e: Event): Promise<void> => {
    // The production UI's <wa-option value=...> comes from
    // `ALL_LANGUAGES.code`, so the runtime value is always a
    // valid LanguageCode. Tests may assign arbitrary strings
    // (e.g. 'chinese'); we accept them at this boundary and let
    // `loadWordlist` throw `UnsourcedLanguageError` if the value
    // doesn't have a registered loader.
    this.selectedLanguage = (e.target as WaSelectElement).value as LanguageCode;
    // #89: the wordlist language also drives the UI locale when we
    // ship a catalog for it (currently en/de); other languages keep
    // the current UI locale.
    void setUiLocale(this.selectedLanguage);
    // Pre-warm the chunk so the first random-word click has no
    // latency. Only for sourced languages — the `<wa-option>` for
    // unsourced ones is disabled in the UI but defend against
    // direct value assignment in tests/scripts.
    if (isSourcedLanguage(this.selectedLanguage)) {
      await this.ensureWordlistLoaded();
    }
  };

  // Tab-switch + QR-source wiring on `passphrase-saved`.
  private _handlePassphraseSaved = (event: Event): void => {
    const saveTab = this.querySelector<HTMLElement>('#save-tab');
    if (saveTab) {
      saveTab.removeAttribute('disabled');
      const tabGroup = this.querySelector<HTMLElement>('wa-tab-group');
      if (tabGroup) {
        tabGroup.setAttribute('active', 'saveqr');
      }
    }

    const printQrComponent = this.querySelector<HTMLElement>('td-print-qr');
    const detail = (event as CustomEvent<NewVaultPassphraseDetail>).detail;
    if (printQrComponent && detail && detail.passphrase) {
      printQrComponent.setAttribute('source', detail.passphrase);
    }
  };

  private _handleNext = (): void => {
    this.nextState(this, 'retrievingDisclosures');
  };

  handleDemo(): void {
    const demoPassphrase = ['one', 'two', 'three', 'four', 'five', 'six'];
    const passphraseFields = this.querySelectorAll<WaInputElement>('wa-input');
    passphraseFields.forEach((field, index) => {
      field.value = demoPassphrase[index] ?? '';
    });
  }

  /**
   * Generate one random word for the given input slot. Lazy-loads the
   * active language's wordlist on first call; routes the random pick
   * through the F3 RNG seam (`pickRandomWord` → `defaultRandom`) so
   * `Math.random` is never used in security-relevant paths.
   */
  async generateRandomWord(wordNumber: number): Promise<void> {
    const wordlist = await this.ensureWordlistLoaded();
    const randomWord = pickRandomWord(wordlist);
    const input = this.querySelector<WaInputElement>(`#word-${wordNumber}`);
    if (input) {
      input.value = randomWord;
    }
  }

  async generateAllRandomWords(): Promise<void> {
    // Load once, then pick six words from the same list.
    const wordlist = await this.ensureWordlistLoaded();
    for (let i = 1; i <= 6; i++) {
      const input = this.querySelector<WaInputElement>(`#word-${i}`);
      if (input) {
        input.value = pickRandomWord(wordlist);
      }
    }
  }

  clearAllWords(): void {
    for (let i = 1; i <= 6; i++) {
      const input = this.querySelector<WaInputElement>(`#word-${i}`);
      if (input) {
        input.value = '';
      }
    }
  }

  savePassphrase(): void {
    const words: string[] = [];
    for (let i = 1; i <= 6; i++) {
      const input = this.querySelector<WaInputElement>(`#word-${i}`);
      if (input && input.value && input.value.trim()) {
        words.push(input.value.trim());
      }
    }

    if (words.length === 6) {
      const passphrase = words.join(' ');
      console.log('Passphrase saved:', passphrase);

      const detail: NewVaultPassphraseDetail = {
        passphrase,
        words,
        language: this.selectedLanguage,
      };

      this.dispatchEvent(
        new CustomEvent<NewVaultPassphraseDetail>('passphrase-saved', {
          detail,
          bubbles: true,
          composed: true,
        })
      );

      this.dispatchEvent(
        new CustomEvent<NewVaultPassphraseDetail>('new-vault-passphrase', {
          detail,
          bubbles: true,
          composed: true,
        })
      );
    } else {
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        alert(t('passphrase.fillAllAlert'));
      }
    }
  }

  override render(): TemplateResult {
    return html`
      <div class="wa-cluster">
        <wa-tab-group>
          <wa-tab id="passphrase-tab" panel="passphrase">
            ${t('passphrase.tab')}
          </wa-tab>
          <wa-tab id="save-tab" panel="saveqr" disabled>
            ${t('passphrase.saveTab')}
          </wa-tab>
          <wa-tab-panel id="passphrase-tab-panel" name="passphrase">
            <h2>${t('passphrase.heading')}</h2>
            <wa-button
              id="clear-all-words"
              variant="danger"
              appearance="outlined"
              @click=${() => this.clearAllWords()}
            >
              <wa-icon name="trash"></wa-icon>
              ${t('passphrase.clearAll')}
            </wa-button>
            <div class="wa-cluster">
              <p>
                ${t('passphrase.intro1')}
                <strong>${t('passphrase.introStrong')}</strong>
                ${t('passphrase.intro2')}
              </p>
              <p>${t('passphrase.intro3')}</p>
              <p>${t('passphrase.intro4')}</p>
            </div>

            <div class="wa-cluster">
              <!-- The select carries its own label: a bare <label> next
                   to a custom element never associates (label[for] can't
                   reach into shadow DOM), so screen readers announced an
                   unnamed combobox (#243). -->
              <wa-select
                id="language-select"
                label=${t('passphrase.languageLabel')}
                .value=${this.selectedLanguage}
                @change=${this._handleLanguageChange}
              >
                ${ALL_LANGUAGES.map(
                  lang => html`
                    <wa-option
                      value=${lang.code}
                      ?selected=${lang.code === this.selectedLanguage}
                      ?disabled=${!lang.isSourced}
                      title=${
                        lang.isSourced ? '' : (lang.unavailableNote ?? '')
                      }
                    >
                      ${
                        lang.isSourced
                          ? lang.name
                          : t('passphrase.comingSoon', {
                              language: lang.name,
                            })
                      }
                    </wa-option>
                  `
                )}
              </wa-select>
            </div>

            <wa-card>
              <!-- Each input owns its label (label[for] cannot associate
                   across a custom element's shadow boundary); the
                   icon-only dice buttons get their accessible name from
                   the labelled wa-icon (#243). -->
              ${[1, 2, 3, 4, 5, 6].map(
                i => html`
                  <div class="wa-cluster">
                    <wa-input
                      id="word-${i}"
                      type="text"
                      label=${t('passphrase.wordLabel', { n: i })}
                      placeholder=${t('passphrase.wordPlaceholder', { n: i })}
                    ></wa-input>
                    <wa-button
                      id="random-word-${i}"
                      size="s"
                      variant="danger"
                      appearance="plain"
                      @click=${() => void this.generateRandomWord(i)}
                    >
                      <wa-icon
                        name="arrows-rotate"
                        label=${t('passphrase.randomWord', { n: i })}
                      ></wa-icon>
                    </wa-button>
                  </div>
                `
              )}

              <div slot="footer" class="wa-split">
                <wa-button
                  id="generate-all"
                  variant="brand"
                  appearance="outlined"
                  @click=${() => void this.generateAllRandomWords()}
                >
                  <wa-icon name="arrows-rotate"></wa-icon>
                  ${t('passphrase.generateAll')}
                </wa-button>
                <wa-button
                  slot="footer"
                  appearance="filled outlined"
                  variant="danger"
                  class="demo-button"
                  @click=${() => this.handleDemo()}
                >
                  <wa-icon name="face-smile-wink" slot="start"></wa-icon>
                  ${t('common.demo')}
                </wa-button>
                <wa-button
                  id="save-passphrase"
                  variant="brand"
                  appearance="outlined filled"
                  @click=${() => this.savePassphrase()}
                >
                  <wa-icon name="paper-plane"></wa-icon>
                  ${t('passphrase.savePrint')}
                </wa-button>
              </div>
            </wa-card>
          </wa-tab-panel>
          <wa-tab-panel id="save-tab-panel" name="saveqr">
            <td-print-qr></td-print-qr>
            <wa-button id="next-btn" variant="brand" @click=${this._handleNext}>
              ${t('passphrase.openDashboard')}
            </wa-button>
          </wa-tab-panel>
        </wa-tab-group>
      </div>
    `;
  }
}
